import React, { useState } from 'react';
import { TrendingUp, TrendingDown, Target, Zap, Calendar, DollarSign, ArrowRight, Building2, Users, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Forecast Section Component
 * Shows predictive insights including overall forecasts, industry breakdowns, and sales rep forecasts
 */
const ForecastSection = ({ data, summary, industryData, salesData }) => {
  const [expandedIndustry, setExpandedIndustry] = useState(null);
  const [expandedRep, setExpandedRep] = useState(null);

  if (!data || !summary) {
    return (
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-slate-200/50 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Forecast</h3>
        <div className="text-center py-8 text-slate-500">
          Not enough data for predictions
        </div>
      </div>
    );
  }

  // Calculate overall forecasts
  const forecast = calculateForecast(data, summary);

  // Calculate industry forecasts
  const industryForecasts = calculateIndustryForecasts(industryData, summary);

  // Calculate sales rep forecasts
  const salesForecasts = calculateSalesForecasts(salesData, summary);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <Target className="text-purple-600" size={24} />
          Forecast & Predictions
        </h2>
        <span className="text-xs text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
          Based on historical trends
        </span>
      </div>

      {/* Overall Forecast Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Volume Forecast */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl p-5 border border-blue-200/50">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-blue-700">Next Month Volume</span>
            <Calendar size={18} className="text-blue-500" />
          </div>
          <div className="text-3xl font-bold text-blue-800">{forecast.nextMonthQuotes}</div>
          <div className="flex items-center gap-1 mt-2 text-sm">
            {forecast.volumeTrend >= 0 ? (
              <TrendingUp size={14} className="text-green-600" />
            ) : (
              <TrendingDown size={14} className="text-red-600" />
            )}
            <span className={forecast.volumeTrend >= 0 ? 'text-green-600' : 'text-red-600'}>
              {forecast.volumeTrend >= 0 ? '+' : ''}{forecast.volumeTrend}% vs current
            </span>
          </div>
        </div>

        {/* Revenue Forecast */}
        <div className="bg-gradient-to-br from-green-50 to-green-100/50 rounded-2xl p-5 border border-green-200/50">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-green-700">Expected Revenue</span>
            <DollarSign size={18} className="text-green-500" />
          </div>
          <div className="text-3xl font-bold text-green-800">{formatCurrency(forecast.expectedRevenue)}</div>
          <div className="text-sm text-green-600 mt-2">
            From {summary.pipelineQuotes || 0} pipeline quotes
          </div>
        </div>

        {/* Win Probability */}
        <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-2xl p-5 border border-purple-200/50">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-purple-700">Win Probability</span>
            <Zap size={18} className="text-purple-500" />
          </div>
          <div className="text-3xl font-bold text-purple-800">{forecast.winProbability}%</div>
          <div className="text-sm text-purple-600 mt-2">
            Historical conversion rate
          </div>
        </div>
      </div>

      {/* Tornado Chart - Revenue Drivers */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-slate-200/50 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Revenue Impact Factors</h3>
        <TornadoChart factors={forecast.revenueFactors} />
      </div>

      {/* Pipeline Flow Prediction */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-slate-200/50 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Pipeline Flow Prediction</h3>
        <PipelineFlow forecast={forecast} summary={summary} />
      </div>

      {/* Industry Forecasts */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-slate-200/50 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Building2 size={20} className="text-blue-600" />
          Forecast by Industry
        </h3>
        {industryForecasts.length > 0 ? (
          <div className="space-y-3">
            {industryForecasts.map((industry, idx) => (
              <IndustryForecastCard
                key={idx}
                industry={industry}
                isExpanded={expandedIndustry === idx}
                onToggle={() => setExpandedIndustry(expandedIndustry === idx ? null : idx)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-4 text-slate-500">No industry data available</div>
        )}
      </div>

      {/* Sales Rep Forecasts */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-slate-200/50 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <Users size={20} className="text-green-600" />
          Forecast by Sales Rep
        </h3>
        {salesForecasts.length > 0 ? (
          <div className="space-y-3">
            {salesForecasts.map((rep, idx) => (
              <SalesRepForecastCard
                key={idx}
                rep={rep}
                isExpanded={expandedRep === idx}
                onToggle={() => setExpandedRep(expandedRep === idx ? null : idx)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-4 text-slate-500">No sales data available</div>
        )}
      </div>
    </div>
  );
};

/**
 * Industry Forecast Card Component
 */
const IndustryForecastCard = ({ industry, isExpanded, onToggle }) => {
  const trendColor = industry.trend >= 0 ? 'text-green-600' : 'text-red-600';
  const trendBg = industry.trend >= 0 ? 'bg-green-100' : 'bg-red-100';

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
            <Building2 size={18} className="text-blue-600" />
          </div>
          <div className="text-left">
            <div className="font-medium text-slate-800">{industry.name}</div>
            <div className="text-sm text-slate-500">{industry.quoteCount} quotes in pipeline</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="font-semibold text-slate-800">{formatCurrency(industry.expectedRevenue)}</div>
            <div className={`text-xs ${trendColor} ${trendBg} px-2 py-0.5 rounded-full inline-flex items-center gap-1`}>
              {industry.trend >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
              {industry.trend >= 0 ? '+' : ''}{industry.trend}%
            </div>
          </div>
          {isExpanded ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 py-3 bg-slate-50 border-t border-slate-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-slate-500">Pipeline ARR</div>
              <div className="font-semibold text-slate-800">{formatCurrency(industry.pipelineARR)}</div>
            </div>
            <div>
              <div className="text-slate-500">Expected Wins</div>
              <div className="font-semibold text-slate-800">{industry.expectedWins}</div>
            </div>
            <div>
              <div className="text-slate-500">Win Rate</div>
              <div className="font-semibold text-slate-800">{industry.winRate}%</div>
            </div>
            <div>
              <div className="text-slate-500">Avg Deal Size</div>
              <div className="font-semibold text-slate-800">{formatCurrency(industry.avgDealSize)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Sales Rep Forecast Card Component
 */
const SalesRepForecastCard = ({ rep, isExpanded, onToggle }) => {
  const trendColor = rep.trend >= 0 ? 'text-green-600' : 'text-red-600';
  const trendBg = rep.trend >= 0 ? 'bg-green-100' : 'bg-red-100';

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white font-semibold text-sm">
            {rep.initials}
          </div>
          <div className="text-left">
            <div className="font-medium text-slate-800">{rep.name}</div>
            <div className="text-sm text-slate-500">{rep.quoteCount} quotes in pipeline</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="font-semibold text-slate-800">{formatCurrency(rep.expectedRevenue)}</div>
            <div className={`text-xs ${trendColor} ${trendBg} px-2 py-0.5 rounded-full inline-flex items-center gap-1`}>
              {rep.trend >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
              {rep.trend >= 0 ? '+' : ''}{rep.trend}%
            </div>
          </div>
          {isExpanded ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 py-3 bg-slate-50 border-t border-slate-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-slate-500">Pipeline ARR</div>
              <div className="font-semibold text-slate-800">{formatCurrency(rep.pipelineARR)}</div>
            </div>
            <div>
              <div className="text-slate-500">Expected Wins</div>
              <div className="font-semibold text-slate-800">{rep.expectedWins}</div>
            </div>
            <div>
              <div className="text-slate-500">Win Rate</div>
              <div className="font-semibold text-slate-800">{rep.winRate}%</div>
            </div>
            <div>
              <div className="text-slate-500">Services OTR</div>
              <div className="font-semibold text-slate-800">{formatCurrency(rep.servicesOTR)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Tornado Chart Component
 * Shows positive/negative impact factors on revenue
 */
const TornadoChart = ({ factors }) => {
  if (!factors || factors.length === 0) {
    return <div className="text-slate-500 text-center py-4">No factor data available</div>;
  }

  const maxImpact = Math.max(...factors.map(f => Math.abs(f.impact)));

  return (
    <div className="space-y-3">
      {/* Scale */}
      <div className="flex items-center justify-between text-xs text-slate-400 px-32">
        <span>-{formatCurrency(maxImpact)}</span>
        <span>0</span>
        <span>+{formatCurrency(maxImpact)}</span>
      </div>

      {factors.map((factor, idx) => {
        const barWidth = (Math.abs(factor.impact) / maxImpact) * 50;
        const isPositive = factor.impact >= 0;

        return (
          <div key={idx} className="flex items-center gap-2">
            {/* Label */}
            <div className="w-28 text-right text-sm font-medium text-slate-700 truncate">
              {factor.name}
            </div>

            {/* Bar container */}
            <div className="flex-1 flex items-center">
              {/* Left side (negative) */}
              <div className="w-1/2 flex justify-end">
                {!isPositive && (
                  <div
                    className="h-7 rounded-l-lg bg-gradient-to-l from-red-400 to-red-500 flex items-center justify-start pl-2"
                    style={{ width: `${barWidth}%` }}
                  >
                    <span className="text-white text-xs font-semibold">
                      -{formatCompact(Math.abs(factor.impact))}
                    </span>
                  </div>
                )}
              </div>

              {/* Center line */}
              <div className="w-px h-8 bg-slate-300" />

              {/* Right side (positive) */}
              <div className="w-1/2 flex justify-start">
                {isPositive && (
                  <div
                    className="h-7 rounded-r-lg bg-gradient-to-r from-green-400 to-green-500 flex items-center justify-end pr-2"
                    style={{ width: `${barWidth}%` }}
                  >
                    <span className="text-white text-xs font-semibold">
                      +{formatCompact(factor.impact)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Pipeline Flow Prediction
 * Shows expected flow from current pipeline to closed deals
 */
const PipelineFlow = ({ forecast, summary }) => {
  const stages = [
    { name: 'Pipeline', value: summary.pipelineQuotes || 0, color: 'bg-blue-500' },
    { name: 'Expected to Send', value: forecast.expectedToSend, color: 'bg-purple-500' },
    { name: 'Predicted Wins', value: forecast.predictedWins, color: 'bg-green-500' },
  ];

  return (
    <div className="flex items-center justify-between">
      {stages.map((stage, idx) => (
        <React.Fragment key={stage.name}>
          <div className="flex-1 text-center">
            <div className={`w-20 h-20 mx-auto rounded-full ${stage.color} flex items-center justify-center mb-2 shadow-lg`}>
              <span className="text-2xl font-bold text-white">{stage.value}</span>
            </div>
            <div className="text-sm font-medium text-slate-700">{stage.name}</div>
          </div>
          {idx < stages.length - 1 && (
            <ArrowRight size={24} className="text-slate-300 flex-shrink-0" />
          )}
        </React.Fragment>
      ))}

      {/* Predicted Revenue */}
      <ArrowRight size={24} className="text-slate-300 flex-shrink-0" />
      <div className="flex-1 text-center">
        <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mb-2 shadow-lg">
          <DollarSign size={28} className="text-white" />
        </div>
        <div className="text-sm font-medium text-slate-700">{formatCurrency(forecast.expectedRevenue)}</div>
      </div>
    </div>
  );
};

/**
 * Calculate overall forecast metrics from historical data
 */
function calculateForecast(volumeTrend, summary) {
  // Calculate average monthly quotes
  const recentMonths = volumeTrend.slice(-3);
  const avgQuotes = recentMonths.length > 0
    ? Math.round(recentMonths.reduce((sum, m) => sum + m.total, 0) / recentMonths.length)
    : 0;

  // Calculate trend (comparing last month to average)
  const lastMonth = volumeTrend[volumeTrend.length - 1]?.total || 0;
  const prevMonth = volumeTrend[volumeTrend.length - 2]?.total || lastMonth;
  const volumeTrendPct = prevMonth > 0 ? Math.round(((lastMonth - prevMonth) / prevMonth) * 100) : 0;

  // Next month prediction (simple linear extrapolation)
  const nextMonthQuotes = Math.max(1, Math.round(avgQuotes * (1 + volumeTrendPct / 100)));

  // Win probability from historical data
  const totalClosed = (summary.acceptedQuotes || 0) + (summary.lostQuotes || 0);
  const winProbability = totalClosed > 0
    ? Math.round((summary.acceptedQuotes / totalClosed) * 100)
    : (summary.pipelineQuotes > 0 ? 50 : 0); // Default 50% if no closed data

  // Expected revenue from pipeline
  const avgDealSize = summary.avgDealSize || 50000;
  const pipelineValue = (summary.totalARR || 0) + (summary.totalServicesOTR || 0);
  const expectedRevenue = Math.round(pipelineValue * (winProbability / 100));

  // Pipeline flow predictions
  const expectedToSend = Math.round((summary.approvedQuotes || 0) + (summary.finalQuotes || 0) * 0.9);
  const predictedWins = Math.round(expectedToSend * (winProbability / 100));

  // Revenue impact factors (simulated based on available data)
  const revenueFactors = [
    { name: 'Pipeline Size', impact: pipelineValue * 0.3 },
    { name: 'Win Rate', impact: (winProbability - 50) * avgDealSize * 0.5 },
    { name: 'Deal Velocity', impact: (summary.sentQuotes || 0) * avgDealSize * 0.2 },
    { name: 'Pending Delays', impact: -(summary.pendingQuotes || 0) * avgDealSize * 0.1 },
    { name: 'Lost Deals', impact: -(summary.lostQuotes || 0) * avgDealSize },
  ].sort((a, b) => b.impact - a.impact);

  return {
    nextMonthQuotes,
    volumeTrend: volumeTrendPct,
    winProbability,
    expectedRevenue,
    expectedToSend,
    predictedWins,
    revenueFactors
  };
}

/**
 * Calculate industry-level forecasts
 */
function calculateIndustryForecasts(industryData, summary) {
  if (!industryData || industryData.length === 0) return [];

  // Overall win rate
  const totalClosed = (summary.acceptedQuotes || 0) + (summary.lostQuotes || 0);
  const overallWinRate = totalClosed > 0
    ? Math.round((summary.acceptedQuotes / totalClosed) * 100)
    : 50;

  return industryData.map(industry => {
    const pipelineARR = industry.pipelineARR || 0;
    const pipelineOTR = industry.pipelineOTR || 0;
    const pipelineCount = industry.pipelineCount || 0;
    const wonARR = industry.wonARR || 0;
    const industryWonCount = industry.wonCount || 0;

    // Calculate industry-specific win rate if we have enough data
    const industryTotal = pipelineCount + industryWonCount;
    const winRate = industryTotal >= 3 && industryWonCount > 0
      ? Math.round((industryWonCount / industryTotal) * 100)
      : overallWinRate;

    const totalPipeline = pipelineARR + pipelineOTR;
    const expectedRevenue = Math.round(totalPipeline * (winRate / 100));
    const expectedWins = Math.round(pipelineCount * (winRate / 100));
    const avgDealSize = pipelineCount > 0 ? Math.round(totalPipeline / pipelineCount) : 0;

    // Calculate trend (compare pipeline to won as a simple proxy)
    const trend = wonARR > 0 && pipelineARR > 0
      ? Math.round(((pipelineARR - wonARR) / wonARR) * 100)
      : pipelineARR > 0 ? 10 : 0;

    return {
      name: industry.industry,
      pipelineARR,
      quoteCount: pipelineCount,
      expectedRevenue,
      expectedWins,
      winRate,
      avgDealSize,
      trend: Math.min(Math.max(trend, -50), 100) // Clamp trend between -50 and 100
    };
  }).filter(i => i.pipelineARR > 0 || i.quoteCount > 0)
    .sort((a, b) => b.expectedRevenue - a.expectedRevenue);
}

/**
 * Calculate sales rep-level forecasts
 */
function calculateSalesForecasts(salesData, summary) {
  if (!salesData || salesData.length === 0) return [];

  // Overall win rate
  const totalClosed = (summary.acceptedQuotes || 0) + (summary.lostQuotes || 0);
  const overallWinRate = totalClosed > 0
    ? Math.round((summary.acceptedQuotes / totalClosed) * 100)
    : 50;

  return salesData.map(rep => {
    const pipelineARR = rep.pipelineARR || 0;
    const pipelineOTR = rep.pipelineOTR || 0;
    const pipelineCount = rep.quotes || 0;
    const wonCount = rep.wonCount || 0;

    // Use rep's actual win rate if available, otherwise overall
    const winRate = rep.winRate || overallWinRate;

    const totalPipeline = pipelineARR + pipelineOTR;
    const expectedRevenue = Math.round(totalPipeline * (winRate / 100));
    const expectedWins = Math.round(pipelineCount * (winRate / 100));

    // Get initials from name
    const nameParts = (rep.name || 'U').split(' ');
    const initials = nameParts.length > 1
      ? `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`
      : nameParts[0].substring(0, 2);

    // Calculate trend based on win performance
    const trend = rep.wonRevenue > 0 && rep.pipelineRevenue > 0
      ? Math.round(((rep.pipelineRevenue - rep.wonRevenue) / rep.wonRevenue) * 100)
      : rep.pipelineRevenue > 0 ? 15 : 0;

    return {
      name: rep.name || 'Unknown',
      initials: initials.toUpperCase(),
      pipelineARR,
      servicesOTR: pipelineOTR,
      quoteCount: pipelineCount,
      expectedRevenue,
      expectedWins,
      winRate,
      trend: Math.min(Math.max(trend, -50), 100) // Clamp trend between -50 and 100
    };
  }).filter(r => r.pipelineARR > 0 || r.quoteCount > 0)
    .sort((a, b) => b.expectedRevenue - a.expectedRevenue);
}

function formatCurrency(value) {
  if (!value) return '$0';
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${Math.round(value)}`;
}

function formatCompact(value) {
  if (!value) return '0';
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
  return `${Math.round(value)}`;
}

export default ForecastSection;
